/*
  BCCaratRenders — makes the gallery's sparkle video tile and 360 spin tile follow the
  carat the shopper picks in the diamond customizer.

  Why: carat is a customizer axis, not a Shopify variant, so every variant's
  `custom.media_assets` points at ONE render folder (for earrings always .../ct-2/). The
  render CDN has sibling folders for each carat (ct-1, ct-3, ct-4 …) with the same layout,
  so showing the chosen carat is just a matter of swapping that one path segment.

  - diamond-customizer.js calls setWeight("4 Cts") whenever the weight is (re)applied.
  - live-view.js and spin-360.js run every render URL through swap(url). Only URLs that
    already contain a /ct-<n>/ segment are touched, so rings (no carat folder) are
    unaffected.
  - A carat folder is only used once its first spin frame has actually loaded (probe()).
    If it 404s, swap() keeps returning the original URL — never a broken tile.
  - Tiles listen for the `bc:carat-change` document event and re-sync themselves.
  - syncStills(): the gallery's other render stills are Shopify-hosted copies of the ct-2
    renders, named State2_<uuid>.webp / State3_<uuid>.webp. Every such <img> (main viewer,
    thumbnail strip, zoom modal) is pointed at {carat folder}State<n>.webp, and restored to
    the Shopify image when the carat goes back to the native folder or the R2 file is
    missing. Other photos (AI front shot, model photo) never match and are left alone.
*/
(function () {
  'use strict';
  if (window.BCCaratRenders) return;

  var CT_RE = /\/ct-\d+(?:\.\d+)?\//;
  var PROBE_FILE = '360/frame_00001.webp'; // same URL the spin poster uses — a shared download
  var folder = null; // e.g. 'ct-4'; null until the customizer reports a weight
  var status = {}; // probed URL -> 'ok' | 'missing' | [callbacks] while in flight
  var STATE_RE = /\/State(\d)[._][^/?#]*$/; // Shopify file name of a ct-2 render still

  function folderFor(weight) {
    var n = parseFloat(String(weight == null ? '' : weight).replace(/^ct-/i, ''));
    return Number.isFinite(n) && n > 0 ? 'ct-' + n : null;
  }

  // The folder URL (…/ct-N/) this url would live in for the current carat, or null when no
  // swap applies (no carat chosen, not a carat-foldered render, or already that carat).
  function targetBase(url) {
    if (!folder || !url || typeof url !== 'string') return null;
    var m = CT_RE.exec(url);
    if (!m || m[0] === '/' + folder + '/') return null;
    return url.slice(0, m.index) + '/' + folder + '/';
  }

  function swapped(url) {
    return url.replace(CT_RE, '/' + folder + '/');
  }

  // Load url once; cb() when settled. Results are cached per URL.
  function check(url, cb) {
    var st = status[url];
    if (st === 'ok' || st === 'missing') { if (cb) cb(); return; }
    if (st) { if (cb) st.push(cb); return; }
    var waiters = (status[url] = cb ? [cb] : []);
    var img = new Image();
    var done = function (ok) {
      status[url] = ok ? 'ok' : 'missing';
      for (var i = 0; i < waiters.length; i++) {
        try { waiters[i](); } catch (e) {}
      }
    };
    img.onload = function () { done(img.naturalWidth > 0); };
    img.onerror = function () { done(false); };
    img.src = url;
  }

  // Render URL of the variant the gallery is showing (live-view config, else spin config).
  function activeRenderUrl() {
    var lv = document.querySelector('live-view-video');
    if (lv && lv.config && lv.config.variants) {
      var d = lv.config.variants[lv.activeVariantId];
      if (d && d.video) return d.video;
    }
    var sp = document.querySelector('spin-360-viewer');
    if (sp && sp.config && sp.config.variants) return sp.config.variants[sp.activeVariantId] || null;
    return null;
  }

  // Gallery <img>s that are Shopify copies of a render still: main viewer, thumbnails and
  // the zoom modal (which lives outside <media-gallery>).
  function stillImgs() {
    var out = [];
    var sel = 'media-gallery .product__media-list img, media-gallery [id^="GalleryThumbnails"] img, product-modal img';
    var imgs = document.querySelectorAll(sel);
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (img.hasAttribute('data-bc-still')) { out.push(img); continue; }
      var src = img.getAttribute('src') || '';
      var set = img.getAttribute('srcset') || '';
      var m = STATE_RE.exec(src.split('?')[0]) || STATE_RE.exec((set.split(/\s|,/)[0] || '').split('?')[0]);
      if (!m) continue;
      // Remember the Shopify original once, so it can be restored exactly.
      img.setAttribute('data-bc-still', m[1]);
      img.setAttribute('data-bc-orig-src', src);
      if (img.hasAttribute('srcset')) img.setAttribute('data-bc-orig-srcset', set);
      if (img.hasAttribute('sizes')) img.setAttribute('data-bc-orig-sizes', img.getAttribute('sizes'));
      out.push(img);
    }
    return out;
  }

  function restoreStill(img) {
    if (!img.hasAttribute('data-bc-carat')) return;
    img.removeAttribute('data-bc-carat');
    img.setAttribute('src', img.getAttribute('data-bc-orig-src'));
    if (img.hasAttribute('data-bc-orig-srcset')) img.setAttribute('srcset', img.getAttribute('data-bc-orig-srcset'));
    if (img.hasAttribute('data-bc-orig-sizes')) img.setAttribute('sizes', img.getAttribute('data-bc-orig-sizes'));
  }

  function setStill(img, url) {
    if (img.getAttribute('data-bc-carat') === url) return;
    img.setAttribute('data-bc-carat', url);
    // Drop the Shopify srcset/sizes, or the browser keeps picking the ct-2 candidates.
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.setAttribute('src', url);
  }

  var stillsQueued = false;
  function syncStillsNow() {
    stillsQueued = false;
    var imgs = stillImgs();
    if (!imgs.length) return;
    var raw = activeRenderUrl();
    var base = targetBase(raw);
    if (!base) { imgs.forEach(restoreStill); return; } // native carat, or not a carat-foldered product
    var want = folder;
    imgs.forEach(function (img) {
      var url = base + 'State' + img.getAttribute('data-bc-still') + '.webp';
      check(url, function () {
        if (folder !== want || targetBase(activeRenderUrl()) !== base) return; // superseded
        if (status[url] === 'ok') setStill(img, url);
        else restoreStill(img);
      });
    });
  }

  var api = {
    // Current carat folder, e.g. 'ct-4' (null = none chosen yet).
    folder: function () { return folder; },

    // Called by the customizer with the selected weight label ("1 Ct", "4 Cts", "1.5 Cts").
    setWeight: function (weight) {
      var next = folderFor(weight);
      api.syncStills(); // also re-runs after an AJAX gallery swap, which calls setWeight again
      if (next === folder) return;
      folder = next;
      try {
        document.dispatchEvent(new CustomEvent('bc:carat-change', { detail: { folder: folder } }));
      } catch (e) {}
      api.syncStills();
    },

    // Point the gallery's render stills at the current carat (see header). Batched to the
    // next task so it runs after an AJAX swap has replaced both the gallery and the modal,
    // and after the tiles have taken a variant change.
    syncStills: function () {
      if (stillsQueued) return;
      stillsQueued = true;
      setTimeout(syncStillsNow, 0);
    },

    // url rewritten to the chosen carat when that carat's folder is confirmed to exist;
    // otherwise the url unchanged.
    swap: function (url) {
      var base = targetBase(url);
      return base && status[base] === 'ok' ? swapped(url) : url;
    },

    // Make sure the chosen carat's folder for `url` has been checked. cb() runs once the
    // check settles (immediately if already known / nothing to check). Callers re-read
    // swap() inside cb and compare against what they currently show.
    probe: function (url, cb) {
      var base = targetBase(url);
      if (!base) { if (cb) cb(); return; }
      check(base + PROBE_FILE, function () {
        status[base] = status[base + PROBE_FILE];
        if (cb) cb();
      });
    }
  };

  window.BCCaratRenders = api;
})();
